/**
 * Runtime wire-up — given an INPA install, a picked IPO, and a built
 * EDIABAS transport, build the full provider graph and start the VM.
 * Exposes a Svelte-store-style reactive `RuntimeHandle` the UI binds
 * to (ScreenBuffer, menu items, status, input dialog).
 *
 * Always wires the real `Ediabas` instance — no mock branch. Callers
 * (typically the connect flow in `connection.ts`) own the transport
 * lifecycle and pass it in; this module knows nothing about Web
 * Serial port pickers, simulation interfaces, etc.
 */

import { parseIpo } from "@emdzej/inpax-parser";
import { VM, MainScheduler } from "@emdzej/inpax-interpreter";
import type { ScreenBuffer } from "@emdzej/inpax-ui-provider-core";
import { WebUIProvider } from "@emdzej/inpax-web-provider";
import {
  NullSimulationProvider,
  NullPrintProvider,
  NullPemProvider,
  NullDtmProvider,
  NullSpsProvider,
} from "@emdzej/inpax-providers/null";
import { EdiabasXProvider, Inp1Adapter } from "@emdzej/inpax-ediabasx-provider";
import {
  BrowserExternalProvider,
  BrowserNativeImportProvider,
  type InpaInstall,
  type IpoEntry,
} from "@emdzej/inpax-web-provider";
import { settings, RUNTIME_TICK_MS_FAST, debugLog } from "./settings.svelte.js";
import { app } from "./state.svelte.js";
import type { IEdiabas } from "@emdzej/ediabasx-core";

export interface RuntimeOptions {
  install: InpaInstall | null;
  ipo: IpoEntry;
  /**
   * Callback that returns the current IEdiabas implementation
   * (either `EmbeddedEdiabas` for local cable or `EdiabasClient`
   * for a remote server). Invoked at `provider.init()` time — the
   * script drives connection lifecycle: when `INPAapiInit` fires,
   * the dispatcher awaits `ui.ensureConnected()` (opens settings,
   * waits for the user); only then does the provider's `init()` run
   * and pull the now-ready IEdiabas.
   *
   * The caller (typically `connection.svelte.ts.getActiveIEdiabas`)
   * may return `null` when nothing's been connected yet; the
   * provider lets the subsequent `connect failed` event surface
   * naturally and the dispatcher's loop turns it into a
   * `job:error` / retry dialog.
   */
  getInstance: () => IEdiabas | null;
}

export interface RuntimeHandle {
  /** Live UI state — Svelte components $effect against the underlying WebUIProvider. */
  ui: WebUIProvider;
  /**
   * Browser-side `external` provider. Exposes `.viewer` $state for
   * `ViewerDialog.svelte` to render the modal that backs `viewopen`.
   */
  external: BrowserExternalProvider;
  /**
   * EDIABAS bridge provider. Exposed so UI components (e.g. the
   * background-I/O indicator) can subscribe to `busy:changed` and
   * other bridge-level events.
   */
  ediabas: EdiabasXProvider;
  /** Buffer the canvas reads each frame. */
  screen: ScreenBuffer;
  /** Stop the scheduler + tear down providers. Idempotent. */
  dispose: () => Promise<void>;
  /** True while the scheduler is running. */
  isRunning: () => boolean;
  /**
   * Subscribe to "frame ready" events — fires once per SCREEN cycle
   * (after the executor has run all LINE blocks and the buffer is in
   * a coherent state), plus on provider state changes before any
   * screen is active (messageboxes, initial title paint, …).
   *
   * The canvas paints from this rather than from `ui.onStateChange`
   * directly: a single LINE block fires many cell-write state changes
   * across multiple event loop ticks (because INPAapiJob is async),
   * and painting on each one shows a half-rewritten LINE — which is
   * what users perceive as flicker. Painting only on cycle boundaries
   * gives an atomic frame.
   *
   * Returns an unsubscribe function.
   */
  onFrameReady: (cb: () => void) => () => void;
}

/**
 * Build the VM + providers and start `inpainit()`.
 *
 * Returns once setup is done; the VM keeps running asynchronously
 * through MainScheduler (every 16 ms tick rebuilds the screen). The
 * caller should call `.dispose()` when the user picks a different
 * script or leaves the page.
 */
export async function startInpaRuntime(
  options: RuntimeOptions
): Promise<RuntimeHandle> {
  /* 1. Read + parse the IPO. VFS's `VirtualFile.arrayBuffer()` works
        the same against FSA, OPFS, and remote HTTP backings. */
  const ipoBytes = new Uint8Array(await options.ipo.handle.arrayBuffer());
  const ipo = parseIpo(ipoBytes);

  // 2. UI provider — feeds the ScreenBuffer that the canvas component
  //    paints from. WebUIProvider also tracks menu items, input dialogs,
  //    title, digital indicators, etc.
  const ui = new WebUIProvider();
  const screen = ui.getScreenBuffer();

  // 3. EDIABAS provider — wraps an `IEdiabas` implementation that the
  //    connection module builds when the user clicks Connect.
  //    Embedded mode → `EmbeddedEdiabas` over Web Serial / J2534 /
  //    Gateway. Client mode → `EdiabasClient` over JSON-RPC.
  //
  //    We use the `getInstance` factory pattern (provider 0.10+) so
  //    the IEdiabas can be built AFTER provider construction — the
  //    IPO mounts first, the script's `INPAapiInit` then triggers
  //    `ui.ensureConnected()`, the user picks a cable / server, and
  //    only THEN does `connection.svelte.ts` build the IEdiabas.
  //    Pre-binding here would require Connect BEFORE picking an IPO,
  //    the old gate flow we removed.
  const ediabasProvider = new EdiabasXProvider({
    getInstance: options.getInstance,
  });

  // Surface job-execution failures to the user, not just the console.
  //
  // The provider's `job:error` event fires when `executeJob` throws —
  // SGBD load failure, transport timeout, JOB_STATUS != OKAY caught by
  // CheckJobStatus, etc. INPA bytecode never sees these directly
  // (the dispatcher catches and surfaces via the event), so without
  // a listener the failure is silently swallowed inside
  // `EdiabasXProvider.job()` and the IPO continues past the failed
  // call with stale results — leading to symptoms like "userbox
  // opens but never closes" (the report-display step runs on an
  // empty/stale result set) or "F1 handler done but no serial".
  //
  // Anchor: KOMBI.IPO!m_fehler ITEM 0 — the F1=Read-faults handler
  // calls `INPAapiFsMode(165, …)` then `INPAapiFsLesen` for KOMBI46R
  // variants. Without this listener, any failure in that chain (e.g.,
  // unsupported FsMode at the cluster, missing job, transport timeout)
  // surfaced as a stuck "Fehlerspeicher lesen" box with zero console
  // breadcrumbs.
  ediabasProvider.on("job:error", ({ code, message }) => {
    const text = `EDIABAS job failed${code !== -1 ? ` (code ${code})` : ""}: ${message}`;
    console.error(`[ediabas/job:error] ${text}`);
    app.error = text;
  });

  // 4. INP1 surface — thin adapter over the same EDIABAS state, so
  //    `INP1apiResultText("VARIANTE", 0, "")` and friends fall through
  //    to the right backend.
  const inp1Provider = new Inp1Adapter(ediabasProvider);

  /* 5. Native imports — INI lookups for F-key bindings etc. Cache
        the INPA.INI / EDIABAS.INI eagerly so the synchronous CALLE
        handler has data to return.

        In client mode (no local install), scripts that depend on
        INI lookups via CALLE degrade to empty results. The null
        stub returns empty arrays so the dispatcher's "no handler"
        warning fires once per import, instead of a hard TypeError. */
  const install = options.install;
  const nativeImports = install && install.ecu
    ? await (async () => {
        const ecuDir = install.ecu!;
        const provider = new BrowserNativeImportProvider({
          install,
          ediabasConfig: {
            ecuPath: ecuDir.name,
            interfaceName: "serial",
            iniPath: "",
          },
        });
        await provider.prefetchIniFiles();
        return provider;
      })()
    : { call: (): unknown[] => [] };

  // 6. VM. Browser-side has no real hardware for simulation / print /
  //    pem / dtm / sps yet — wire the null providers so scripts that
  //    touch those system functions get a quiet no-op instead of a
  //    TypeError from a `null` provider reference. `external` is the
  //    real `BrowserExternalProvider` because INPA scripts use it to
  //    show fault-store reports (viewopen). The dispatcher's
  //    `INPAapiFsLesen` handler (in @emdzej/inpax-dispatcher) runs the
  //    job, formats the result sets via the canonical INPA template,
  //    and writes the report through `external.writeFile`. The
  //    subsequent `viewopen("na_fs.tmp", …)` reads that virtual file
  //    back via `external.viewOpen` and the `ViewerDialog` component
  //    renders it as a modal.
  const externalProvider = new BrowserExternalProvider();
  // Tick interval driven by user settings (see ConfigPanel → Developer).
  // Debug mode on → use the user's chosen `tickMs` so the log stream
  // is legible while diagnosing. Debug mode off → fast default that
  // mirrors real INPA's "no fixed tick, yield to the event loop"
  // behaviour. Captured at runtime construction; changing the
  // setting takes effect on the next script mount, not mid-run.
  const TICK_MS = settings.debugMode ? settings.tickMs : RUNTIME_TICK_MS_FAST;
  const vm = new VM(ipo, {
    runtime: {
      ui,
      ediabas: ediabasProvider,
      inp1: inp1Provider,
      simulation: new NullSimulationProvider(),
      print: new NullPrintProvider(),
      pem: new NullPemProvider(),
      dtm: new NullDtmProvider(),
      external: externalProvider,
      sps: new NullSpsProvider(),
      nativeImports,
    },
    debug: false,
    screenExecutor: { tickInterval: TICK_MS },
  });

  // 7. Scheduler. INPA screens with frequentFlag=true are designed for
  //    cooperative repaint via INPA's OnIdle handler; MainScheduler is
  //    the equivalent here. Throttled to TICK_MS while debugging.
  const scheduler = new MainScheduler(vm, { tickInterval: TICK_MS, debug: false });

  // Wire menu events from TUI provider → scheduler so F-key clicks
  // queue menu item handlers without blocking the main scheduler loop.
  ui.on("menu:select", ({ itemNum }) => {
    const menuHandle = ui.state.menuHandle;
    if (menuHandle === null) return;
    const handler = findMenuItemHandler(ipo, menuHandle, itemNum);
    if (handler) {
      scheduler.queueMenuAction(itemNum, async () => {
        debugLog(`[menu:select] running handler for F${itemNum}`);
        try {
          // `executeMenuItem` (not `executeBlock`) preserves the
          // active menu's persistent ExecutionContext, so the
          // handler's `local[i]` references resolve against the
          // values the menu's INIT body left on the stack. Using
          // `executeBlock` here would create a fresh context with
          // an empty stack and trip on `PUSHREF local[0]`.
          await vm.executeMenuItem(handler);
          debugLog(`[menu:select] handler for F${itemNum} done`);
        } catch (err) {
          console.error(`[menu:select] handler for F${itemNum} threw:`, err);
        }
      });
    }
  });

  // Don't initialise the provider here — the script's `INPAapiInit`
  // now drives that path through `ui.ensureConnected()` →
  // `provider.init()`. Calling it twice would `ediabas.connect()`
  // before the transport callback has anything to return.

  scheduler.start();

  // Run __inpa_startup__ → inpainit() asynchronously. Errors are
  // surfaced through the provider's job:error event; we don't await
  // here because the VM's run loop is open-ended.
  void vm.run().catch((err: unknown) => {
    console.error("[inpax-web/runtime] VM error:", err);
  });

  // Frame-ready fan-out — see `RuntimeHandle.onFrameReady`. Triggers:
  //   - Pre-screen / setup phase: every provider `state:changed`
  //     (title, F-key labels, messageboxes — whatever inpainit() writes
  //     before the first SCREEN block).
  //   - Once the screen executor's `cycle:complete` has fired once,
  //     state:changed is dropped and cycle becomes the sole trigger.
  //     state:changed fires per cell write and spans many event-loop
  //     ticks across one LINE block, so painting on it shows a half-
  //     rewritten line — exactly the Battery/Ignition flicker symptom.
  const frameListeners = new Set<() => void>();
  const dispatchFrame = () => {
    for (const cb of frameListeners) {
      try {
        cb();
      } catch (err) {
        console.error("[inpax-web/runtime] frame listener threw:", err);
      }
    }
  };

  let offStateChange: (() => void) | null = ui.onStateChange(dispatchFrame);

  // The active ScreenExecutor changes whenever the script calls
  // `setscreen` — both during inpainit's setup and every time an
  // F-key handler swaps screens. Each new executor is a fresh
  // EventEmitter, so we have to (un)subscribe `cycle:complete` on
  // each swap. Polling `tick:complete` is the cheapest signal that
  // tells us "the VM just observed the screen executor field" —
  // we compare against `attachedScreen` to detect a swap.
  let attachedScreen: ReturnType<VM["getScreenExecutor"]> = null;
  let offCycle: (() => void) | null = null;
  const onCycle = () => {
    if (offStateChange) {
      offStateChange();
      offStateChange = null;
    }
    dispatchFrame();
  };
  scheduler.on("tick:complete", () => {
    const screenExec = vm.getScreenExecutor();
    if (screenExec === attachedScreen) return;
    offCycle?.();
    offCycle = null;
    attachedScreen = screenExec;
    if (screenExec) {
      screenExec.on("cycle:complete", onCycle);
      offCycle = () => screenExec.off("cycle:complete", onCycle);
    }
  });

  let disposed = false;
  return {
    ui,
    external: externalProvider,
    ediabas: ediabasProvider,
    screen,
    isRunning: () => !disposed && scheduler.isRunning(),
    onFrameReady: (cb) => {
      frameListeners.add(cb);
      return () => frameListeners.delete(cb);
    },
    dispose: async () => {
      if (disposed) return;
      disposed = true;
      offStateChange?.();
      scheduler.stop();
      try {
        await ediabasProvider.end();
      } catch {
        /* ignore */
      }
    },
  };
}

/**
 * Find the function block for an F-key inside the currently active
 * menu. Each `MenuItemBlock` stores its slot in `header.flags`
 * (F1=1 .. F10=10, Shift+F1=11 .. Shift+F10=20). The slot is NOT the
 * array index — MS43.IPO m_main, for example, declares item index 8
 * with flags=18 ("Gesamt" on Shift+F8), so an index-based lookup
 * routes the click to the wrong handler.
 */
function findMenuItemHandler(
  ipo: ReturnType<typeof parseIpo>,
  menuHandle: number,
  itemNum: number
): Parameters<VM["executeBlock"]>[0] | null {
  const menu = ipo.menus.get(menuHandle);
  if (!menu) return null;
  const item = menu.items.find((m) => m.header.flags === itemNum);
  return item?.func ?? null;
}
